import { useTaskProcessor } from '@eluve/appointment-tasks';
import { Box, NewButton, toast } from '@eluve/components';
import {
  useAppointmentActivity,
  useAppointmentStatus,
  useAppointmentTranscript,
  useIsAppointmentReadonly,
  useSummary,
} from '@eluve/frontend-appointment-hooks';
import { AppointmentStatusTypesLookup } from '@eluve/graphql-types';
import { useCaptureEvent } from '@eluve/posthog-react';
import {
  FeatureFlaggedComponent,
  useSelectedMicrophoneDevice,
} from '@eluve/smart-blocks';

import { useAppointmentDialogs } from '../AppointmentDialogProvider';
import { useStartAppointmentRecording } from '../machines/transcription/hooks/useStartAppointmentRecording';
import { useTranscriptionMachineSelector } from '../machines/transcription/hooks/useTranscriptionMachine';

import { UploadAudioFileButton } from './UploadAudioFileButton';

interface TranscriptionControlsProps {
  tenantId: string;
  appointmentId: string;
  onGenerateNote?: () => void;
}

export const AppointmentTranscriptionIdleControls: React.FC<
  TranscriptionControlsProps
> = ({ tenantId, appointmentId, onGenerateNote }) => {
  const isReadonly = useIsAppointmentReadonly();
  const appointmentStatus = useAppointmentStatus();
  const [selectedMicId] = useSelectedMicrophoneDevice();
  const { segments } = useAppointmentTranscript();
  const { isSummaryAvailable } = useSummary();
  const { isSummarizationInProgress } = useAppointmentActivity();

  const { openOverwriteSummaryDialog } = useAppointmentDialogs();
  const { taskProcessor } = useTaskProcessor();

  const hasTranscript = segments.some((s) => s.transcript);

  const isGeneratingNote =
    useTranscriptionMachineSelector((s) =>
      s.matches({ InSession: 'SessionEnded' }),
    ) || isSummarizationInProgress;

  const { startAppointmentRecording, isStartingAppointmentRecording } =
    useStartAppointmentRecording({
      tenantId,
      appointmentId,
      selectedMicId,
    });

  const captureEvent = useCaptureEvent({
    appointmentId,
  });

  const handleResumeClick = () => {
    startAppointmentRecording();
  };

  const isCompleted =
    appointmentStatus === AppointmentStatusTypesLookup.COMPLETED;

  const handleGenerateNoteClick = () => {
    if (isGeneratingNote) {
      return;
    }

    if (!hasTranscript) {
      toast.error('No transcription found');
      return;
    }

    captureEvent('ended_session');
    onGenerateNote?.();

    if (isSummaryAvailable) {
      openOverwriteSummaryDialog(appointmentId);
    } else {
      taskProcessor.addTask({
        type: 'TASK.GENERATE_NOTE',
        appointmentId,
        userCompletedAt: new Date().toISOString(),
      });
    }
  };

  if (isReadonly) {
    return null;
  }

  return (
    <Box hStack>
      <FeatureFlaggedComponent flag="ALLOW_AUDIO_FILE_UPLOAD_FOR_APPOINTMENT">
        <UploadAudioFileButton appointmentId={appointmentId} buttonSize="m" />
      </FeatureFlaggedComponent>
      <NewButton
        disabled={isGeneratingNote}
        loading={isStartingAppointmentRecording}
        onClick={handleResumeClick}
        text="Resume Session"
        type="subtle"
      />
      <NewButton
        data-accessibility-label="generate-note-button"
        disabled={isCompleted || !hasTranscript}
        loading={isGeneratingNote}
        onClick={handleGenerateNoteClick}
        text="Generate Note"
      />
    </Box>
  );
};
